import { motion, AnimatePresence } from 'framer-motion';
import { X, Watch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const sizeData = [
  { size: '36mm', wristSize: '13-15 cm', description: 'Petite / Delicate wrists' },
  { size: '38mm', wristSize: '15-16 cm', description: 'Small / Slim wrists' },
  { size: '39mm', wristSize: '16-17 cm', description: 'Medium / Standard wrists' },
  { size: '40mm', wristSize: '17-18 cm', description: 'Medium-Large wrists' },
  { size: '41mm', wristSize: '17-19 cm', description: 'Large / Athletic wrists' },
  { size: '42mm', wristSize: '18-20 cm', description: 'Large / Bold presence' },
  { size: '43mm', wristSize: '19-21 cm', description: 'Extra Large / Statement' },
  { size: '44mm', wristSize: '20-22 cm', description: 'Oversized / Sport' },
  { size: '46mm', wristSize: '21+ cm', description: 'Maximum / Dive watches' },
];

const SizeGuide = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="size-guide-modal">
        <DialogHeader className="border-b border-white/5 pb-4">
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-3">
            <Watch className="w-6 h-6 text-gold-500" />
            Watch Size Guide
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 space-y-8">
          {/* Introduction */}
          <div className="space-y-4">
            <p className="text-white/60 leading-relaxed">
              Finding the perfect watch size is essential for both comfort and aesthetics. 
              The ideal case diameter depends on your wrist circumference and personal style preference.
            </p>
          </div>

          {/* How to Measure */}
          <div className="bg-white/5 p-6 border border-white/5">
            <h3 className="font-serif text-lg text-white mb-4">How to Measure Your Wrist</h3>
            <ol className="space-y-3 text-white/60">
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gold-500 text-black text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>Wrap a flexible measuring tape around your wrist, just below the wrist bone.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gold-500 text-black text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>Keep the tape snug but not tight—you should be able to fit a finger underneath.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gold-500 text-black text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>Note the measurement in centimeters and refer to the chart below.</span>
              </li>
            </ol>
          </div>

          {/* Size Chart */}
          <div>
            <h3 className="font-serif text-lg text-white mb-4">Size Chart</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-gold-500">Case Size</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-gold-500">Wrist Size</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-gold-500">Recommended For</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeData.map((row, index) => (
                    <motion.tr
                      key={row.size}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="py-4 px-4 font-serif text-white">{row.size}</td>
                      <td className="py-4 px-4 text-white/60">{row.wristSize}</td>
                      <td className="py-4 px-4 text-white/40 text-sm">{row.description}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tips */}
          <div className="space-y-4">
            <h3 className="font-serif text-lg text-white">Expert Tips</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white/5 p-4 border border-white/5">
                <p className="text-gold-500 text-sm font-bold mb-2">Classic Style</p>
                <p className="text-white/50 text-sm">For a timeless, understated look, choose a case that sits within your wrist width.</p>
              </div>
              <div className="bg-white/5 p-4 border border-white/5">
                <p className="text-gold-500 text-sm font-bold mb-2">Bold Statement</p>
                <p className="text-white/50 text-sm">For a contemporary, bold presence, opt for a case that slightly extends beyond your wrist.</p>
              </div>
              <div className="bg-white/5 p-4 border border-white/5">
                <p className="text-gold-500 text-sm font-bold mb-2">Dress Watches</p>
                <p className="text-white/50 text-sm">Formal timepieces typically range 36-40mm for an elegant, refined appearance.</p>
              </div>
              <div className="bg-white/5 p-4 border border-white/5">
                <p className="text-gold-500 text-sm font-bold mb-2">Sport/Dive</p>
                <p className="text-white/50 text-sm">Larger cases (42-46mm) provide better legibility and rugged aesthetics.</p>
              </div>
            </div>
          </div>

          {/* Need Help */}
          <div className="text-center pt-4 border-t border-white/5">
            <p className="text-white/50 mb-4">Still unsure? Our concierge team is here to help.</p>
            <Button
              onClick={onClose}
              className="bg-gold-500 text-black hover:bg-gold-400 rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
              data-testid="size-guide-close-btn"
            >
              Close Guide
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SizeGuide;
